import React from 'react'
import { Layers, Clock, Volume2, Scissors } from 'lucide-react'

/**
 * Common Element Properties Component
 * Handles common properties like layers and timeline information for all element types
 */
const CommonElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  templateType
}) => {
  if (!element) return null

  const isVideoTemplate = templateType === 'video' || templateType === 'video'

  // Get timeline information for video templates
  const getTimelineInfo = () => {
    if (!isVideoTemplate || !element.time) return null

    const startTime = element.time.start || 0
    const duration = element.time.duration || 0
    const endTime = startTime + duration

    return {
      start: startTime,
      duration: duration,
      end: endTime
    }
  }

  const timelineInfo = getTimelineInfo()

  // Format time for display
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`
    } else {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = (seconds % 60).toFixed(1)
      return `${minutes}m ${remainingSeconds}s`
    }
  }

  return (
    <div className="space-y-4">
      {/* Layers Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center space-x-2 mb-3">
          <Layers className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-700">Layer Order</h3>
        </div>
        
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm text-gray-600 font-medium">Layer</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="0"
              max="1000"
              value={element.layer || 0}
              onChange={(e) => updateElementProperty(selectedElement, 'layer', parseInt(e.target.value) || 0)}
              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-xs text-gray-500">Higher = Top</span>
          </div>
        </div>
        
        <div className="mt-2 text-xs text-gray-500">
          Elements with higher layer numbers appear on top
        </div>
      </div>

      {/* Timeline Information Section - Only for video templates, exclude audio (handled by AudioElementProperties) */}
      {isVideoTemplate && timelineInfo && element.type !== 'audio' && (
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-medium text-blue-700">Timeline Information</h3>
          </div>
          
          <div className="space-y-2">
            {/* Start Time */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-blue-600 font-medium">Start Time</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={timelineInfo.start}
                  onChange={(e) => updateElementProperty(selectedElement, 'time', {
                    ...element.time,
                    start: parseFloat(e.target.value) || 0
                  })}
                  className="w-20 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-xs text-blue-500">{formatTime(timelineInfo.start)}</span>
              </div>
            </div>

            {/* Duration */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-blue-600 font-medium">Duration</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={timelineInfo.duration}
                  onChange={(e) => updateElementProperty(selectedElement, 'time', {
                    ...element.time,
                    duration: parseFloat(e.target.value) || 0.1
                  })}
                  className="w-20 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-xs text-blue-500">{formatTime(timelineInfo.duration)}</span>
              </div>
            </div>

            {/* End Time (Read-only) */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-blue-600 font-medium">End Time</label>
              <span className="text-sm font-medium text-blue-700">{formatTime(timelineInfo.end)}</span>
            </div>

            {/* Timeline Visual Indicator */}
            <div className="mt-3">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-blue-200 rounded-full h-2 relative">
                  <div 
                    className="bg-blue-500 h-2 rounded-full absolute"
                    style={{
                      left: `${Math.min((timelineInfo.start / Math.max(timelineInfo.end, 1)) * 100, 100)}%`,
                      width: `${Math.min((timelineInfo.duration / Math.max(timelineInfo.end, 1)) * 100, 100)}%`
                    }}
                  ></div>
                </div>
                <span className="text-xs text-blue-500">Timeline</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Settings for Video Templates - Exclude audio (handled by AudioElementProperties) */}
      {isVideoTemplate && (element.type === 'video' || element.type === 'tts' || element.type === 'sound-effect') && (
        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
          <div className="flex items-center space-x-2 mb-3">
            <Volume2 className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-medium text-green-700">Audio Settings</h3>
          </div>
          
          <div className="space-y-2">
            {/* Volume */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-green-600 font-medium">Volume</label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={element.settings?.volume || 1}
                  onChange={(e) => updateElementProperty(selectedElement, 'settings', {
                    ...element.settings,
                    volume: parseFloat(e.target.value)
                  })}
                  className="flex-1 max-w-[120px]"
                />
                <span className="text-xs text-green-500 min-w-[35px]">
                  {Math.round((element.settings?.volume || 1) * 100)}%
                </span>
              </div>
            </div>

            {/* Fade In */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-green-600 font-medium">Fade In</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={element.settings?.fadeIn || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'settings', {
                    ...element.settings,
                    fadeIn: parseFloat(e.target.value) || 0
                  })}
                  className="w-16 px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <span className="text-xs text-green-500">s</span>
              </div>
            </div>

            {/* Fade Out */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-green-600 font-medium">Fade Out</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={element.settings?.fadeOut || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'settings', {
                    ...element.settings,
                    fadeOut: parseFloat(e.target.value) || 0
                  })}
                  className="w-16 px-2 py-1 text-sm border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <span className="text-xs text-green-500">s</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trim Settings for Video Elements - Exclude audio (handled by AudioElementProperties) */}
      {isVideoTemplate && element.type === 'video' && element.trim && (
        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
          <div className="flex items-center space-x-2 mb-3">
            <Scissors className="w-4 h-4 text-orange-600" />
            <h3 className="text-sm font-medium text-orange-700">Trim Settings</h3>
          </div>
          
          <div className="space-y-2">
            {/* Trim Start */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-orange-600 font-medium">Trim Start</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={element.trim.start || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'trim', {
                    ...element.trim,
                    start: parseFloat(e.target.value) || 0
                  })}
                  className="w-16 px-2 py-1 text-sm border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <span className="text-xs text-orange-500">s</span>
              </div>
            </div>

            {/* Trim End */}
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-orange-600 font-medium">Trim End</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={element.trim.end || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'trim', {
                    ...element.trim,
                    end: parseFloat(e.target.value) || 0
                  })}
                  className="w-16 px-2 py-1 text-sm border border-orange-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <span className="text-xs text-orange-500">s</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* AI Field — for image elements this is shown in ImageElementProperties
          (grouped with "AI Image Prompt"), so render it here only for
          non-image elements to avoid duplication. */}
      {element.type !== 'image' && (
        <AIFieldInput
          element={element}
          selectedElement={selectedElement}
          updateElementProperty={updateElementProperty}
        />
      )}
    </div>
  )
}

/**
 * Sanitize an AI field name: alphanumeric and underscore only, no spaces,
 * max 60 characters. Shared with ImageElementProperties.
 */
export const sanitizeAiField = (value) =>
  String(value || '').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 60)

/**
 * AI Field input. The AI field is a field NAME: alphanumeric + underscore only,
 * no spaces, max 60 characters.
 */
export const AIFieldInput = ({ element, selectedElement, updateElementProperty }) => {
  const value = element.ai_field || ''
  return (
    <div className="bg-gray-50 p-3 rounded-lg">
      <h3 className="text-sm font-medium text-gray-700 mb-3">AI Field</h3>
      <div className="space-y-2">
        <input
          type="text"
          value={value}
          maxLength={60}
          onChange={(e) => updateElementProperty(selectedElement, 'ai_field', sanitizeAiField(e.target.value))}
          placeholder="e.g. gm_quote"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">Field name: letters, numbers and underscore only.</p>
          <p className="text-xs text-gray-400">{value.length}/60</p>
        </div>
      </div>
    </div>
  )
}

export default CommonElementProperties
